import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../../../api/client.js";
import { RE_STATE_LABEL, type ReProjectSummaryDto } from "./re-api.js";

/** FIX-011 / RE Stage 4 - the user's saved RE projects (the platform owner sees the whole workspace's): open one to continue where it stopped. */
export function ReProjectsList({ tenantId }: { readonly tenantId: string }) {
  const [rows, setRows] = useState<ReProjectSummaryDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // FIX-062 (owner 16:41 IL): display only - an uploaded source that came from a Deep Research handoff is labelled with the reel's #number.
  // One read-only GET on the Deep Research API after the list loads; any failure keeps "Upload". No RE API, flow or data is touched.
  const [drSource, setDrSource] = useState<ReadonlyMap<string, string>>(new Map());
  useEffect(() => {
    let cancelled = false;
    api.get<{ projects: ReProjectSummaryDto[] }>(`/re/${tenantId}/projects`)
      .then((r) => {
        if (cancelled) return;
        setRows(r.projects);
        if (!r.projects.some((p) => p.sourceKind === "upload")) return;
        api.get<{ handoffs?: Array<{ code: string; num: number | null; sourceVideoId: string | null; reProjectId: string | null }> }>(`/research/v4/${encodeURIComponent(tenantId)}/handoffs?target=RE`)
          .then((h) => {
            if (cancelled || !Array.isArray(h?.handoffs)) return;
            const m = new Map<string, string>();
            for (const x of h.handoffs) { const label = `Deep Research ${x.num !== null ? `#${x.num}` : x.code}`; if (x.reProjectId) m.set(`p:${x.reProjectId}`, label); if (x.sourceVideoId) m.set(`s:${x.sourceVideoId}`, label); }
            setDrSource(m);
          }, () => undefined);
      })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? "Could not load your RE projects." : "Could not load your RE projects."); });
    return () => { cancelled = true; };
  }, [tenantId]);
  return (
    <section data-testid="re-projects">
      <p><Link to="/generate/create/re">← New RE project</Link></p>
      <h3>My RE projects</h3>
      {error !== null ? <div className="error-banner" role="alert">{error}</div> : null}
      {rows === null && error === null ? <p className="muted">Loading…</p> : null}
      {rows !== null && rows.length === 0 ? <p className="muted" data-testid="re-projects-empty">No RE projects yet.</p> : null}
      {rows !== null && rows.length > 0 ? (
        <table>
          <thead><tr><th>Persona</th><th>Status</th><th>Source</th><th>Updated</th><th /></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} data-testid="re-project-row" data-project={p.id}>
                <td>{p.personaDisplayName ?? p.personaId}</td>
                <td>{RE_STATE_LABEL[p.state]}{p.requestInFlight ? " …" : ""}</td>
                <td>{p.sourceKind === "srr" ? "Approved Reel" : drSource.get(`p:${p.id}`) ?? (p.sourceVideoId ? drSource.get(`s:${p.sourceVideoId}`) : undefined) ?? "Upload"}</td>
                <td>{new Date(p.updatedAt).toLocaleString()}</td>
                <td><Link to={`/generate/create/re/projects/${encodeURIComponent(p.id)}`} data-testid="re-open-project">Open</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
